import { Link } from 'react-router';
import { usePwlJugMissing } from '@hooks/register/use-pwl-jug-missing';
import { useLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import type { IPwlJugMissingModel } from '@/types/history/pwl-jug-missing';
import PageLoader from '@components/page-loader';

const PwlJugMissing = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, sendAnother, isSubmitting, submitError } = usePwlJugMissing(registrationId, editKey);

    return (
        <div className="container-fluid">
            <PageLoader isLoading={isLoading} error={error}>
                {!error && (
                    <PwlJugMissingContent
                        model={model}
                        onSendAnother={sendAnother}
                        isSubmitting={isSubmitting}
                        submitError={submitError}
                    />
                )}
            </PageLoader>
        </div>
    );
};

interface IPwlJugMissingContentProps {
    model: IPwlJugMissingModel;
    onSendAnother: () => Promise<void>;
    isSubmitting: boolean;
    submitError: string | null;
}

const PwlJugMissingContent = ({ model, onSendAnother, isSubmitting, submitError }: IPwlJugMissingContentProps) => {
    const t = useLocalize(model.languageData);

    const hasReachedJugLimit = model.totalJugsShipped >= 2;
    const canSendAnother = !model.lastJugShippedRecently && !hasReachedJugLimit;

    return (
        <div className="row row-margined">
            <div className="col-xs-12 col-sm-7 col-sm-offset-0">
                <div className="row row-margined">
                    <div className="col-xs-12">
                        <h1>{t('DashLabel_WeApologizeForThisInconvenience', 'We apologize for this inconvenience')}.</h1>

                        {model.lastJugShippedRecently ? (
                            <p>
                                {t(
                                    'DashLabel_PleaseWait48HoursBeforeIndicatingThatYouDidNotReceiveTheJug',
                                    'Please wait 48 hours before indicating that you did not receive the jug',
                                )}
                                .
                            </p>
                        ) : hasReachedJugLimit ? (
                            <p>
                                {t(
                                    'DashLabel_ItAppearsAsIfMultipleCollectionJugsHaveAlreadyBeenShipped',
                                    'It appears as if multiple collection jugs have already been shipped to your location. Unfortunately, we',
                                )}{' '}
                                {t(
                                    'DashLabel_HaveALimitOfSendingNoMoreThanTwoJugsPerPersonInOrderToProceed',
                                    'have a limit of sending no more than two jugs per person. In order to proceed with your 24-hour urine',
                                )}{' '}
                                {t(
                                    'DashLabel_CollectionPleaseUseALargeResealableVesselSuchAsACleanedOut',
                                    'collection, please use a large resealable vessel such as a cleaned out 2-liter bottle or milk jug',
                                )}
                                .
                            </p>
                        ) : (
                            <p>
                                {t(
                                    'DashLabel_IfYouWouldLikeYouCanUseACleanedOut2LiterBottleAnd/OrClean',
                                    'If you would like,  you can use a cleaned out 2-liter bottle and/or cleaned out milk jug(s) for the 24-hour urine collection',
                                )}
                                .{' '}
                                {t(
                                    'DashLabel_IfYouWishToHaveAnother24HourUrineCollectionJugShippedToYou',
                                    'If you wish to have another 24-hour urine collection jug shipped to you, you can have one more shipped by clicking the link below',
                                )}
                                :
                            </p>
                        )}
                    </div>
                </div>

                {submitError && (
                    <div className="row row-margined">
                        <div className="col-xs-12">
                            <div className="alert alert-danger">{submitError}</div>
                        </div>
                    </div>
                )}

                <div className="row row-margined">
                    <div className="col-xs-12">
                        {canSendAnother && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                style={{ marginRight: 10 }}
                                disabled={isSubmitting}
                                onClick={() => void onSendAnother()}
                            >
                                {t('DashLabel_YesSendMeAnotherJug', 'Yes, send me another jug')}
                            </button>
                        )}

                        <Link className={canSendAnother ? 'btn btn-default' : 'btn btn-primary'} to="/register/donate">
                            {t('DashLabel_BackToDashboard', 'Back to Dashboard')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PwlJugMissing;
